import React, { useCallback } from 'react';
import {
  Button,
  Callout,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  ListItem,
  Text,
  ThemeToggle,
  Tooltip,
  VisuallyHidden,
} from '@/components/ui';

import { usePermissions } from '@/contexts/PermissionsContext';
import { useLogout } from '@/services/authHooks';
import { useAuth } from '@/contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';

import {
  IconCheck,
  IconChevronDown,
  IconHelp,
  IconLifebuoy,
  IconLogout,
  IconSettings,
  IconUserCircle,
} from '@tabler/icons-react';
import { ROUTES } from '@/config/routes.ts';

const UserMenu: React.FC = React.memo(() => {
  const { user } = useAuth();
  const location = useLocation();
  const { mutate: logout, isPending } = useLogout();
  const { activeRole, availableRoles, setActiveRole } = usePermissions();

  let triggerLabel: React.JSX.Element | string = 'Unknown user';
  let role = '';

  if (user) {
    role = activeRole?.name || '';
    if (user.id !== 'none') {
      triggerLabel = (
        <ListItem
          // primary={`${user?.first_name} ${user?.last_name}`}
          primary={`${user?.first_name} ${user?.last_name}`}
          secondary={role}
          endIcon={<IconChevronDown />}
        />
      );
    }
  }

  const handleLogout = useCallback(() => {
    if (!user) {
      return;
    }
    logout();
  }, [logout, user]);

  const handleRoleChange = useCallback(
    (newRole: typeof activeRole) => {
      if (newRole) {
        setActiveRole(newRole);
      }
    },
    [setActiveRole]
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Button
          asChild
          variant="soft"
          className="user-menu__trigger-button"
          size="small"
          style={{ margin: 0 }}
        >
          {triggerLabel}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" variant="soft">
        {user && (
          <DropdownMenuItem asChild disabled>
            <ListItem
              primary={`${user.first_name} ${user.last_name} `}
              secondary={user.email}
              className="user-menu__card "
              style={{
                cursor: 'default',
                pointerEvents: 'none',
                minWidth: 220,
              }}
            >
              <div style={{ color: '#aaa', fontSize: 12, marginTop: 2 }}>
                ID: {user.id}
              </div>
            </ListItem>
          </DropdownMenuItem>
        )}

        <DropdownMenuItem asChild disabled>
          <div
            style={{
              padding: 'var(--space-2)',
              minWidth: 220,
            }}
            className="user-menu__theme-toggle"
          >
            <ThemeToggle variant="segmented" />
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuSub asChild>
          <DropdownMenuSubTrigger asChild className="list-item hover-effect">
            <ListItem
              inverse
              secondary="Current Role"
              primary={`${activeRole?.name || 'None'}`}
            />
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="role-switcher">
            {availableRoles.length > 0 ? (
              availableRoles.map(role => (
                <Tooltip
                  key={role.id}
                  content={
                    <>
                      <Text size="3xsmall" color="gray" mb="2">
                        Role description:
                      </Text>
                      <Text size="2xsmall">{role.description}</Text>
                    </>
                  }
                  side="right"
                  align="center"
                  maxWidth="300px"
                  width="200px"
                  delayDuration={300}
                  style={{ padding: 'var(--space-3)' }}
                >
                  <DropdownMenuItem
                    asChild
                    disabled={activeRole?.id === role.id}
                  >
                    <ListItem
                      startIcon={
                        <IconUserCircle
                          style={{
                            color:
                              activeRole?.id === role.id
                                ? 'currentColor'
                                : 'var(--gray-9)',
                          }}
                        />
                      }
                      endIcon={
                        activeRole?.id === role.id ? <IconCheck /> : undefined
                      }
                      primary={role.name}
                      selected={activeRole?.id === role.id}
                      onClick={() => handleRoleChange(role)}
                    />
                  </DropdownMenuItem>
                </Tooltip>
              ))
            ) : (
              <div style={{ padding: 'var(--space-3)' }}>
                <Callout color="info" size="small" title="No available roles">
                  {user?.current_study
                    ? `You don't have any available roles for the "${user.current_study.name}" study.`
                    : "You don't have any available roles for this study."}
                </Callout>
              </div>
            )}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        {user && user.id !== 'none' && (
          <DropdownMenuItem asChild>
            <ListItem
              as={Link}
              to={ROUTES.settings}
              primary="Personal Settings"
              startIcon={<IconSettings />}
              selected={location.pathname === ROUTES.settings}
            />
          </DropdownMenuItem>
        )}
        <VisuallyHidden>
          <DropdownMenuSeparator />

          <DropdownMenuItem asChild>
            <ListItem
              primary="Support"
              startIcon={<IconLifebuoy />}
              as={Link}
              to="#support"
            />
          </DropdownMenuItem>

          <DropdownMenuItem asChild>
            <ListItem
              primary="Help"
              startIcon={<IconHelp />}
              as={Link}
              to="#help"
            />
          </DropdownMenuItem>
        </VisuallyHidden>

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <ListItem
            className="hover-effect"
            primary={isPending ? 'Logging out...' : 'Logout'}
            startIcon={<IconLogout />}
            onClick={handleLogout}
            disabled={isPending}
          />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

export default UserMenu;
